<template>
  <div class="my-container"  aria-label="">
    <!-- ✅ Hero Section -->
    <header
      class="page-header"
      :style="{ backgroundImage: `url(${headerImage})` }"
    >
      <h1 v-if="translatedTitle">🎓 {{ translatedTitle }}</h1>
      <h1 v-else>Documentation Étudiant</h1>
      <p class="p-header">
        {{
          translatedDescription ||
          "Tout ce que vous devez savoir pour réussir votre apprentissage sur Longoka."
        }}
      </p>
    </header>

    <!-- ✅ Sections de documentation -->
    <section class="doc-section">
      <h2><i class="fas fa-book-open"></i> Accès aux Cours</h2>
      <p>
        En tant qu'étudiant sur Longoka, vous pouvez explorer une variété de
        cours couvrant plusieurs disciplines. Accédez aux supports pédagogiques
        et aux exercices interactifs.
      </p>
      <NuxtLink to="/courses" class="cta-button">Explorer les Cours</NuxtLink>
    </section>

    <section class="doc-section">
      <h2><i class="fas fa-chart-line"></i> Suivi de Progression</h2>
      <p>
        Suivez vos progrès, terminez les leçons et obtenez des certificats en
        fonction de votre avancement.
      </p>
      <NuxtLink to="/student/dashboard" class="cta-button"
        >Voir ma progression</NuxtLink
      >
    </section>

    <section class="doc-section">
      <h2><i class="fas fa-headset"></i> Support et Assistance</h2>
      <p>
        Besoin d'aide ? Rejoignez notre forum ou contactez le support technique
        pour obtenir des réponses à vos questions.
      </p>
      <NuxtLink to="/support" class="cta-button">Obtenir de l'aide</NuxtLink>
    </section>

    <section class="doc-section">
      <h2><i class="fas fa-question-circle"></i> FAQ Étudiants</h2>
      <p>
        Trouvez des réponses aux questions fréquemment posées sur l'utilisation
        des cours et des fonctionnalités de Longoka.
      </p>
      <NuxtLink to="/faq" class="cta-button">Consulter la FAQ</NuxtLink>
    </section>
    <ExploreSection />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useNuxtApp } from "#imports";
import { useHead } from "#app";

const { $translate } = useNuxtApp();
const translatedTitle = ref("");
const translatedDescription = ref("");
const translatedKeywords = ref("");
const headerImage = useState("headerImage");

async function translateSEO() {
  try {
    const userLang = navigator.language.split("-")[0];
    const targetLang = [
      "fr",
      "en",
      "es",
      "de",
      "it",
      "pt",
      "ru",
      "zh",
    ].includes(userLang)
      ? userLang
      : "fr";

    translatedTitle.value = await $translate(
      "Documentation Étudiant",
      "fr",
      targetLang
    );
    translatedDescription.value = await $translate(
      "Tout ce que vous devez savoir pour réussir votre apprentissage sur Longoka.",
      "fr",
      targetLang
    );
    translatedKeywords.value = await $translate(
      "étudiant, apprentissage, cours, progression, certificat, Longoka, support, FAQ",
      "fr",
      targetLang
    );
  } catch (error) {
    console.error("❌ Erreur de traduction SEO :", error);
  }
}

onMounted(async () => {
  await translateSEO();
});

useHead(() => ({
  title: computed(() => `${translatedTitle.value} | Longoka`),
  meta: [
    { name: "description", content: translatedDescription.value },
    { name: "keywords", content: translatedKeywords.value },
    { name: "robots", content: "index, follow, max-image-preview:large" },
    { property: "og:title", content: translatedTitle.value },
    { property: "og:description", content: translatedDescription.value },
    { property: "og:type", content: "website" },
    { property: "og:image", content: "/images/documentation-student.jpg" },
    { name: "twitter:title", content: translatedTitle.value },
    { name: "twitter:description", content: translatedDescription.value },
    { name: "application-name", content: "Longoka" },
  ],
}));
</script>

<style scoped>
.doc-section {
  padding: 20px;
  margin-bottom: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
.cta-button {
  display: inline-block;
  padding: 10px 15px;
  background: #1e88e5;
  color: white;
  text-decoration: none;
  border-radius: 5px;
  transition: background 0.3s ease;
}
.cta-button:hover {
  background: #1565c0;
}
@media (max-width: 768px) {
  .doc-section {
    padding: 15px;
  }
}
</style>
